import {groupRecord, schoolAreaRecord, schoolGradeRecord, townRecord} from '@/utils/const';
import { ColumnType } from 'antd/es/table';

export interface ColumnConfig<T> extends ColumnType<T> {
  active?: boolean;
  disabled?: boolean;
  name?:string;
  fathername?: string;
  width?: number;
  areaCode?: string;
  children?: ColumnConfig<T>[];
  title: string;
  /** 均分/平均分列，导出与展示时保留两位小数 */
  isAvg?: boolean;
}
type ColumnsOptions = {
  showGradeLine?: boolean;
  isJunior?: boolean;
}

export const ALL_COURSE_AVG_COLUMN_SETTING_CONFIG = {
  storageKey: 'examCenter:analysis:allCourseAvg:columnSetting',
  version: 2,
  defaultInactiveDataIndexList: [
    'allPassStuCount',
    'allPassRate',
    'allPassRateRank',
    't200StuCount',
    't200Rate',
    't200RateRank',
    't300StuCount',
    't300Rate',
    't300RateRank',
    'p15StuCount',
    'p15Rate',
    'p15RateRank'
  ]
} as const;

const allCourseAvgDefaultInactiveDataIndexSet = new Set<string>(
  ALL_COURSE_AVG_COLUMN_SETTING_CONFIG.defaultInactiveDataIndexList
);

const isAllCourseAvgColumnDefaultActive = (dataIndex: string) =>
  !allCourseAvgDefaultInactiveDataIndexSet.has(dataIndex);

export const getAllCourseAvgColumns: (data: SchoolStat, options?: ColumnsOptions) => ColumnConfig<SchoolStat>[] = (data,options) => {
  const { showGradeLine, isJunior } = options || {};
  const gradeLineFields = [
    { title: '普高人数', active: true, dataIndex: 'seniorLineStuCount' },
    { title: '普高率', active: true, dataIndex: 'seniorLineRate' },
    { title: '普高率排名', active: true, dataIndex: 'seniorLineRateRank' }
  ];
  // 达标率
  const standardRateFields = [
    { title: '阅读达标合格率', active: true, dataIndex: 'chinesePassRate', width: 120 },
    { title: '阅读达标合格率排名', active: true, dataIndex: 'chinesePassRateRank', width: 150 },
    { title: '数学达标合格率', active: true, dataIndex: 'mathPassRate', width: 120 },
    { title: '数学达标合格率排名', active: true, dataIndex: 'mathPassRateRank', width: 150 }
  ];
  return [
    { title: '校码', active: true, dataIndex: 'schoolCode' },
    {
      title: '片区',
      active: true,
      dataIndex: 'areaCode' ,
      width: 60,
      render: (_, p) => {
        if (['片区', '乡镇'].includes(p.areaCode)) return p.areaCode;
        if (p.isGroup) return groupRecord[p.groupCode] || '-';
        if (p.isTown) return townRecord[p.townCode] || '-';
        if (p.isSchool || p.isArea) return schoolAreaRecord[p.areaCode] || '-';
        else return '全市';
      }
    },
    { title: '学校', active: true, dataIndex: 'schoolShortname', width: 120 },
    { title: '最高分', dataIndex: 'maxScore', active: true },
    { title: '总人数/实考/缺考', active: true, dataIndex: 'numberCount', width: 120, render(_, p) {
      return `${p.totalStuCount}/${p.presentStuCount}/${p.absentStuCount}`;
    }},
    ...(showGradeLine ? gradeLineFields : []),
    { title: '全科合格人数', active: isAllCourseAvgColumnDefaultActive('allPassStuCount'), dataIndex: 'allPassStuCount', width: 120 },
    { title: '全科合格率', active: isAllCourseAvgColumnDefaultActive('allPassRate'), dataIndex: 'allPassRate' },
    { title: '全科合格率排名', active: isAllCourseAvgColumnDefaultActive('allPassRateRank'), dataIndex: 'allPassRateRank', width: 120 },
    ...(isJunior ? standardRateFields : []),
    {
      title: `前100(${data.scoreGroupMap.t100})`,
      active: true,
      name: 't100',
      width: 180,
      children: [
        { title: '人数', active: true, fathername: 't100', dataIndex: 't100StuCount' },
        { title: '百分比', active: true, fathername: 't100', dataIndex: 't100Rate' },
        { title: '排名', active: true, fathername: 't100', dataIndex: 't100RateRank' }
      ]
    },
    {
      title: `前200(${data.scoreGroupMap.t200})`,
      active: isAllCourseAvgColumnDefaultActive('t200StuCount'),
      name: 't200',
      width: 180,
      children: [
        { title: '人数', active: isAllCourseAvgColumnDefaultActive('t200StuCount'), fathername: 't200', dataIndex: 't200StuCount' },
        { title: '百分比', active: isAllCourseAvgColumnDefaultActive('t200Rate'), fathername: 't200', dataIndex: 't200Rate' },
        { title: '排名', active: isAllCourseAvgColumnDefaultActive('t200RateRank'), fathername: 't200', dataIndex: 't200RateRank' }
      ]
    },
    {
      title: `前300(${data.scoreGroupMap.t300})`,
      active: isAllCourseAvgColumnDefaultActive('t300StuCount'),
      name: 't300',
      width: 180,
      children: [
        { title: '人数', active: isAllCourseAvgColumnDefaultActive('t300StuCount'), fathername: 't300', dataIndex: 't300StuCount' },
        { title: '百分比', active: isAllCourseAvgColumnDefaultActive('t300Rate'), fathername: 't300', dataIndex: 't300Rate' },
        { title: '排名', active: isAllCourseAvgColumnDefaultActive('t300RateRank'), fathername: 't300', dataIndex: 't300RateRank' }
      ]
    },
    {
      title: `前400(${data.scoreGroupMap.t400})`,
      active: true,
      name: 't400',
      width: 180,
      children: [
        { title: '人数', active: true, fathername: 't400', dataIndex: 't400StuCount' },
        { title: '百分比', active: true, fathername: 't400', dataIndex: 't400Rate' },
        { title: '排名', active: true, fathername: 't400', dataIndex: 't400RateRank' }
      ]
    },
    {
      title: `前500(${data.scoreGroupMap.t500})`,
      active: true,
      name: 't500',
      width: 180,
      children: [
        { title: '人数', active: true, fathername: 't500', dataIndex: 't500StuCount' },
        { title: '百分比', active: true, fathername: 't500', dataIndex: 't500Rate' },
        { title: '排名', active: true, fathername: 't500', dataIndex: 't500RateRank' }
      ]
    },
    {
      title: `前1000(${data.scoreGroupMap.t1000})`,
      active: true,
      name: 't1000',
      width: 180,
      children: [
        { title: '人数', active: true, fathername: 't1000', dataIndex: 't1000StuCount' },
        { title: '百分比', active: true, fathername: 't1000', dataIndex: 't1000Rate' },
        { title: '排名', active: true, fathername: 't1000', dataIndex: 't1000RateRank' }
      ]
    },
    {
      title: `前10%(${data.scoreGroupMap.p10})`,
      active: true,
      name: 'p10',
      width: 180,
      children: [
        { title: '人数', active: true, fathername: 'p10', dataIndex: 'p10StuCount' },
        { title: '百分比', active: true, fathername: 'p10', dataIndex: 'p10Rate' },
        { title: '排名', active: true, fathername: 'p10', dataIndex: 'p10RateRank' }
      ]
    },
    {
      title: `前15%(${data.scoreGroupMap.p15})`,
      active: isAllCourseAvgColumnDefaultActive('p15StuCount'),
      name: 'p15',
      width: 180,
      children: [
        { title: '人数', active: isAllCourseAvgColumnDefaultActive('p15StuCount'), fathername: 'p15', dataIndex: 'p15StuCount' },
        { title: '百分比', active: isAllCourseAvgColumnDefaultActive('p15Rate'), fathername: 'p15', dataIndex: 'p15Rate' },
        { title: '排名', active: isAllCourseAvgColumnDefaultActive('p15RateRank'), fathername: 'p15', dataIndex: 'p15RateRank' }
      ]
    },
    {
      title: `前2500(${data.scoreGroupMap.t2500})`,
      active: true,
      name: 't2500',
      width: 180,
      children: [
        { title: '人数', active: true, fathername: 't2500', dataIndex: 't2500StuCount' },
        { title: '百分比', active: true, fathername: 't2500', dataIndex: 't2500Rate' },
        { title: '排名', active: true, fathername: 't2500', dataIndex: 't2500RateRank' }
      ]
    },
    {
      title: `前4000(${data.scoreGroupMap.t4000})`,
      active: true,
      name: 't4000',
      width: 180,
      children: [
        { title: '人数', active: true, fathername: 't4000', dataIndex: 't4000StuCount' },
        { title: '百分比', active: true, fathername: 't4000', dataIndex: 't4000Rate' },
        { title: '排名', active: true, fathername: 't4000', dataIndex: 't4000RateRank' }
      ]
    },
    {
      title: `后1000(${data.scoreGroupMap.e1000})`,
      active: true,
      width: 180,
      name: 'e1000',
      children: [
        { title: '人数', active: true, fathername: 'e1000', dataIndex: 'e1000StuCount'},
        { title: '百分比', active: true, fathername: 'e1000', dataIndex: 'e1000Rate' },
        { title: '排名', active: true, fathername: 'e1000', dataIndex: 'e1000RateRank' }
      ]
    }
  ];
};


export interface ColumnSettingParams {
  title: string;
  active: boolean;
  disabled?: boolean;
  dataIndex: string;
}
export const getColumnSetting: (options?: ColumnsOptions) => ColumnSettingParams[] = options => {
  const { showGradeLine, isJunior } = options || {};
  const gradeLineFields = [
    { title: '普高人数', active: true, dataIndex: 'seniorLineStuCount' },
    { title: '普高率', active: true, dataIndex: 'seniorLineRate' },
    { title: '普高率排名', active: true, dataIndex: 'seniorLineRateRank' }
  ];
  const standardRateFields = [
    { title: '阅读达标合格率', active: true, dataIndex: 'chinesePassRate' },
    { title: '阅读达标合格率排名', active: true, dataIndex: 'chinesePassRateRank' },
    { title: '数学达标合格率', active: true, dataIndex: 'mathPassRate' },
    { title: '数学达标合格率排名', active: true, dataIndex: 'mathPassRateRank' }
  ];
  return [
    { title: '片区', active: true, disabled: true, dataIndex: 'areaCode' },
    { title: '学校', active: true, disabled: true, dataIndex: 'schoolShortname' },
    { title: '最高分', active: true, dataIndex: 'maxScore' },
    { title: '总人数/实考/缺考', active: true, dataIndex: 'numberCount' },
    { title: '总分均分', active: true, dataIndex: 'WHOLE_COURSE_TFullScoreCourseAvg' },
    { title: '总分排名', active: true, dataIndex: 'WHOLE_COURSE_TCourseRank' },
    { title: '各科均分', active: true, dataIndex: 'CourseAvg' },
    { title: '各科排名', active: true, dataIndex: 'CourseRank' },
    ...(showGradeLine ? gradeLineFields : []),
    { title: '全科合格人数', active: isAllCourseAvgColumnDefaultActive('allPassStuCount'), dataIndex: 'allPassStuCount' },
    { title: '全科合格率', active: isAllCourseAvgColumnDefaultActive('allPassRate'), dataIndex: 'allPassRate' },
    { title: '全科合格率排名', active: isAllCourseAvgColumnDefaultActive('allPassRateRank'), dataIndex: 'allPassRateRank' },
    ...(isJunior ? standardRateFields : []),
    { title: '前100人数', active: true, dataIndex: 't100StuCount' },
    { title: '前100百分比', active: true, dataIndex: 't100Rate' },
    { title: '前100排名', active: true, dataIndex: 't100RateRank' },
    { title: '前200人数', active: isAllCourseAvgColumnDefaultActive('t200StuCount'), dataIndex: 't200StuCount' },
    { title: '前200百分比', active: isAllCourseAvgColumnDefaultActive('t200Rate'), dataIndex: 't200Rate' },
    { title: '前200排名', active: isAllCourseAvgColumnDefaultActive('t200RateRank'), dataIndex: 't200RateRank' },
    { title: '前300人数', active: isAllCourseAvgColumnDefaultActive('t300StuCount'), dataIndex: 't300StuCount' },
    { title: '前300百分比', active: isAllCourseAvgColumnDefaultActive('t300Rate'), dataIndex: 't300Rate' },
    { title: '前300排名', active: isAllCourseAvgColumnDefaultActive('t300RateRank'), dataIndex: 't300RateRank' },
    { title: '前400人数', active: true, dataIndex: 't400StuCount' },
    { title: '前400百分比', active: true, dataIndex: 't400Rate' },
    { title: '前400排名', active: true, dataIndex: 't400RateRank' },
    { title: '前500人数', active: true, dataIndex: 't500StuCount' },
    { title: '前500百分比', active: true, dataIndex: 't500Rate' },
    { title: '前500排名', active: true, dataIndex: 't500RateRank' },
    { title: '前1000人数', active: true, dataIndex: 't1000StuCount' },
    { title: '前1000百分比', active: true, dataIndex: 't1000Rate' },
    { title: '前1000排名', active: true, dataIndex: 't1000RateRank' },
    { title: '前10%人数', active: true, dataIndex: 'p10StuCount' },
    { title: '前10%百分比', active: true, dataIndex: 'p10Rate' },
    { title: '前10%排名', active: true, dataIndex: 'p10RateRank' },
    { title: '前15%人数', active: isAllCourseAvgColumnDefaultActive('p15StuCount'), dataIndex: 'p15StuCount' },
    { title: '前15%百分比', active: isAllCourseAvgColumnDefaultActive('p15Rate'), dataIndex: 'p15Rate' },
    { title: '前15%排名', active: isAllCourseAvgColumnDefaultActive('p15RateRank'), dataIndex: 'p15RateRank' },
    { title: '前2500人数', active: true, dataIndex: 't2500StuCount' },
    { title: '前2500百分比', active: true, dataIndex: 't2500Rate' },
    { title: '前2500排名', active: true, dataIndex: 't2500RateRank' },
    { title: '前4000人数', active: true, dataIndex: 't4000StuCount' },
    { title: '前4000百分比', active: true, dataIndex: 't4000Rate' },
    { title: '前4000排名', active: true, dataIndex: 't4000RateRank' },
    { title: '后1000人数', active: true, dataIndex: 'e1000StuCount' },
    { title: '后1000百分比', active: true, dataIndex: 'e1000Rate' },
    { title: '后1000排名', active: true, dataIndex: 'e1000RateRank' }
  ];
};

/**
 * 总分分段分析
 */

export const allCourseSplitColumns: Omit<ColumnConfig<AllCourseSplitSchoolStat>, 'active'>[] = [
  { title: '校码', dataIndex: 'schoolCode' },
  {
    title: '片区',
    dataIndex: 'areaCode',
    render: (_, p) => schoolAreaRecord[p.areaCode] || '全市',
    width: 80
  },
  { title: '学校', dataIndex: 'schoolShortname', width: 120 },
  { title: '参考人数', dataIndex: 'presentStuCount' },
  { title: '最高分', dataIndex: 'maxScore' },
  { title: '最低分', dataIndex: 'minScore' }
];

// 单科成绩分析
export const singleCourseColumn: ColumnConfig<SingleCourseAnalysisStat>[] = [
  { title: '校码', dataIndex: 'schoolCode', active: true },
  {
    title: '片区',
    dataIndex: 'areaCode',
    render: (_, p) => schoolAreaRecord[p.areaCode] || '全市',
    width: 80,
    active: true
  },
  { title: '学校', dataIndex: 'schoolShortname', width: 120, active: true },
  { title: '参考人数', dataIndex: 'presentStuCount', active: true },
  { title: '平均分', dataIndex: 'avgScore', active: true },
  { title: '最高分', dataIndex: 'maxScore', active: true },
  { title: '最低分', dataIndex: 'minScore', active: true }
];

export const getSingleColumnSetting: () => ColumnSettingParams[] = () => {
  return [
    { title: '校码', active: true, disabled: true, dataIndex: 'schoolCode' },
    { title: '片区', active: true, disabled: true, dataIndex: 'areaCode' },
    { title: '学校', active: true, disabled: true, dataIndex: 'schoolShortname' },
    { title: '参考人数', active: true, dataIndex: 'presentStuCount' },
    { title: '平均分', active: true, dataIndex: 'avgScore' },
    { title: '最高分', active: true, dataIndex: 'maxScore' },
    { title: '最低分', active: true, dataIndex: 'minScore' },
    { title: '人数', active: true, disabled: true, dataIndex: 'scoreCount' },
    { title: '百分比', active: false, dataIndex: 'scoreRate' },
    { title: '排名', active: false, dataIndex: 'scoreRank' }
  ];
};

export const courseModuleTypeRecord: Record<string, string> = {
  'ENGLISH_REGULAR': '英基',
  'ENGLISH_ORAL': '英口',
  'CHINESE_REGULAR': '语基',
  'CHINESE_COMPOSITION': '语作'
};
// 四率分析
export const fourRatesColumns: Omit<ColumnConfig<FourRateAnalysisStat>, 'active'>[] = [
  { title: '校码', dataIndex: 'schoolCode' },
  { title: '片区', dataIndex: 'areaCode',
    render: (_, p) => schoolAreaRecord[p.areaCode] || '全市'
  },
  { title: '学校', dataIndex: 'schoolShortname', width: 120 },
  { title: '总人数/实考/缺考', dataIndex: 'numberCount', width: 120, render(_, p) {
    return `${p.totalStuCount}/${p.presentStuCount}/${p.absentStuCount}`;
  } },
  { title: '满分', dataIndex: 'fullScore' },
  { title: '平均分', dataIndex: 'avgScore', children: [], width: 180 },
  { title: '最高分', dataIndex: 'maxScore' },
  { title: '最低分', dataIndex: 'minScore' },
  { title: '全距', dataIndex: 'range' },
  { title: '优秀率', dataIndex: 'excellentRate' },
  { title: '良好率', dataIndex: 'goodRate' },
  { title: '合格率', dataIndex: 'passRate' },
  { title: '低分率', dataIndex: 'lowscoreRate' },
  { title: '标准差', dataIndex: 'std' },
  { title: '众数', dataIndex: 'maxCountScore', render: (_, p) => {
    return p.maxCountScoreList ? p.maxCountScoreList[0] : '';
  } },
  { title: '贡献值', dataIndex: 'avgDiffPass' },
  { title: '排名', dataIndex: 'avgRank' }
];
